// ── moving in: what each thing brought in is (MOVING-IN-PLAN.md §3) ─────────
//
// John, 2026-10-24: "if you have anything somewhere, like your past agents.md
// files or just anywhere where you have been keeping your old memories, there
// should be a way to ingest that properly." An import read chatgpt and claude
// exports and nothing else, and every other file was "a note". Now each source
// says what KIND of thing it is, because the sort reads them differently:
//
//   instructions  how the person wants their ais to work (AGENTS.md, CLAUDE.md,
//                 GEMINI.md, .cursorrules, .cursor/rules/*.mdc, .windsurfrules,
//                 copilot-instructions.md, custom instructions): personality
//                 lines first, and any project context inside
//   memory        an ai's saved memory of the person (MEMORY.md and its topical
//                 files, chatgpt's saved memories, gemini's saved info): facts it
//                 reports, and project notes
//   notes         anything else: a notes export, a journal, a doc
//   chat          a chatgpt / claude export (lib/self/parseExport)
//   archive       an innernet archive (lib/export/readArchive)
//
// and which TOOL an instruction file is for (CLAUDE.md → claude), so a line it
// holds is offered to that tool's identity and no other.
//
// Pure and client-safe: the import sheet reads files in the browser with it,
// the cli the same, and the server checks what it is sent.

export const SOURCE_KINDS = ['instructions', 'memory', 'notes', 'chat', 'archive'] as const
export type SourceKind = (typeof SOURCE_KINDS)[number]

/** the identity keys an instruction file can be for (lib/identity/types IDENTITY_TOOLS), '' = every agent */
export type SourceTool = '' | 'claude' | 'chatgpt' | 'cursor' | 'gemini' | 'copilot' | 'codex' | 'windsurf'

export interface IntakeSource {
  /** the file's name or path as the person sees it (never sent back to a model as more than a label) */
  title: string
  kind: SourceKind
  /** for an instruction file: the tool it tells how to work ('' = every agent) */
  for?: SourceTool
  text: string
  /** when it was written, when known (ISO) */
  dated?: string | null
}

/** what a file's name alone says it is, or null when its name says nothing */
function kindByName(name: string): { kind: SourceKind; for?: SourceTool } | null {
  const path = name.replace(/\\/g, '/').toLowerCase()
  const base = path.split('/').pop() ?? path
  if (base === 'agents.md' || base === 'agent.md') return { kind: 'instructions', for: '' }
  if (base === 'claude.md' || base === 'claude.local.md') return { kind: 'instructions', for: 'claude' }
  if (base === 'gemini.md') return { kind: 'instructions', for: 'gemini' }
  if (base === '.cursorrules' || /(^|\/)\.cursor\/rules\//.test(path) || base.endsWith('.mdc')) return { kind: 'instructions', for: 'cursor' }
  if (base === '.windsurfrules' || /(^|\/)\.windsurf\/rules\//.test(path)) return { kind: 'instructions', for: 'windsurf' }
  if (base === 'copilot-instructions.md' || /(^|\/)\.github\/instructions\//.test(path)) return { kind: 'instructions', for: 'copilot' }
  if (base === 'codex.md') return { kind: 'instructions', for: 'codex' }
  if (/custom[-_ ]?instructions/.test(base)) return { kind: 'instructions', for: '' }
  if (base === 'memory.md' || /(^|\/)(\.claude\/projects\/[^/]+\/)?memory\//.test(path)) return { kind: 'memory' }
  if (/saved[-_ ]?(memories|info)|chatgpt[-_ ]?memor/.test(base)) return { kind: 'memory' }
  if (base === 'conversations.json') return { kind: 'chat' }
  return null
}

const INSTRUCTION_VOICE = /^(?:\s*[-*]\s*)?(?:you are|you're|always|never|do not|don't|prefer|when (?:i|you)|use |avoid |respond|reply|write |keep )/im
const MEMORY_VOICE = /\b(?:the user|user's|the person) (?:is|has|prefers|likes|uses|works|wants|lives|was)\b/i
const FRONT_MATTER = /^---\s*\n[\s\S]*?\n---\s*\n/

/**
 * What a source is: its name first (exact names and rule-file paths), then its
 * words (an ai's memory reads "the user prefers…"; an instruction file speaks to
 * an ai: "always…", "never…", "you are…"). Anything else is notes.
 */
export function kindOf(name: string, text: string): { kind: SourceKind; for?: SourceTool } {
  const byName = kindByName(name)
  if (byName) return byName
  const head = text.slice(0, 4000).replace(FRONT_MATTER, '')
  const memoryLines = (head.match(new RegExp(MEMORY_VOICE.source, 'gi')) ?? []).length
  if (memoryLines >= 2) return { kind: 'memory' }
  const lines = head.split('\n').filter((l) => l.trim())
  const told = lines.filter((l) => INSTRUCTION_VOICE.test(l)).length
  if (lines.length >= 3 && told / lines.length >= 0.4) return { kind: 'instructions', for: '' }
  return { kind: 'notes' }
}

/** the files the sheet reads as text (anything else is skipped, said so, before anything is sent) */
export const TEXT_FILE = /\.(md|mdc|markdown|txt|text|json|jsonl|yaml|yml|toml|org|rst|csv|html?)$|(^|\/)\.(cursorrules|windsurfrules)$/i

/** a file's text is worth sending: not empty, not binary */
export function readable(text: string): boolean {
  if (!text.trim()) return false
  const sample = text.slice(0, 2000)
  // NUL bytes or a high share of replacement characters: a binary file read as text
  const bad = (sample.match(/\u0000|�/g) ?? []).length
  return bad / Math.max(1, sample.length) < 0.02
}

/** one source's words, at most this long (a longer file is split on its headings, never cut) */
export const SOURCE_PART = 12_000
/** one sort call's words at most (several sources, or the parts of one) */
export const BATCH_CHARS = 40_000
/** sources per sort call at most */
export const BATCH_SOURCES = 24
/** what one move-in takes in all, before it asks to send the rest in another */
export const RUN_CHARS = 600_000

/** Split a long source on its headings (then paragraphs), so no part is longer than `max`. Pure. */
export function splitSource(s: IntakeSource, max = SOURCE_PART): IntakeSource[] {
  if (s.text.length <= max) return [s]
  const blocks = s.text.split(/\n(?=#{1,3} )/)
  const parts: string[] = []
  let cur = ''
  const push = () => {
    if (cur.trim()) parts.push(cur)
    cur = ''
  }
  for (const b of blocks) {
    if (b.length > max) {
      push()
      // a heading-less run: split on blank lines, then hard at max as a last resort
      let buf = ''
      for (const para of b.split(/\n\s*\n/)) {
        if ((buf + '\n\n' + para).length > max && buf) {
          parts.push(buf)
          buf = ''
        }
        if (para.length > max) {
          for (let i = 0; i < para.length; i += max) parts.push(para.slice(i, i + max))
        } else buf = buf ? `${buf}\n\n${para}` : para
      }
      if (buf.trim()) parts.push(buf)
      continue
    }
    if ((cur + '\n' + b).length > max) push()
    cur = cur ? `${cur}\n${b}` : b
  }
  push()
  return parts.map((text, i) => ({ ...s, title: parts.length > 1 ? `${s.title} (part ${i + 1} of ${parts.length})` : s.title, text }))
}

/** the order a pile is read in: how they want their ais to work first, then an ai's memory, notes, chats */
const KIND_ORDER: Record<SourceKind, number> = { instructions: 0, memory: 1, notes: 2, chat: 3, archive: 4 }

/** Batch a pile for the sort: kind order, each long source split, ≤ BATCH_CHARS and BATCH_SOURCES a call. Pure. */
export function batchSources(sources: ReadonlyArray<IntakeSource>): IntakeSource[][] {
  const parts = [...sources]
    .map((s, i) => ({ s, i }))
    .sort((a, b) => KIND_ORDER[a.s.kind] - KIND_ORDER[b.s.kind] || a.i - b.i)
    .flatMap(({ s }) => splitSource(s))
  const batches: IntakeSource[][] = []
  let cur: IntakeSource[] = []
  let chars = 0
  for (const p of parts) {
    if (cur.length && (chars + p.text.length > BATCH_CHARS || cur.length >= BATCH_SOURCES)) {
      batches.push(cur)
      cur = []
      chars = 0
    }
    cur.push(p)
    chars += p.text.length
  }
  if (cur.length) batches.push(cur)
  return batches
}

/** a source as the server takes it from a caller: checked, trimmed, its kind its own or read from it */
export function cleanSource(raw: unknown): IntakeSource | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const text = typeof r.text === 'string' ? r.text : typeof r.content === 'string' ? r.content : ''
  if (!readable(text)) return null
  const title = String(r.title ?? r.label ?? r.path ?? 'a note')
    .replace(/[\r\n]+/g, ' ')
    .trim()
    .slice(0, 160) || 'a note'
  const read = kindOf(title, text)
  const kind = (SOURCE_KINDS as readonly string[]).includes(String(r.kind)) ? (String(r.kind) as SourceKind) : read.kind
  const forTool = typeof r.for === 'string' ? (r.for.toLowerCase() as SourceTool) : read.for
  const dated = typeof r.dated === 'string' && Number.isFinite(Date.parse(r.dated)) ? new Date(Date.parse(r.dated)).toISOString() : null
  return { title, kind, ...(kind === 'instructions' ? { for: forTool ?? '' } : {}), text, ...(dated ? { dated } : {}) }
}

/** how the sheet names a kind to the person */
export const KIND_WORDS: Record<SourceKind, string> = {
  instructions: 'how your ais should work',
  memory: "an ai's memory of you",
  notes: 'notes',
  chat: 'a chat export',
  archive: 'an innernet archive',
}
